import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { xor } from 'lodash'

import { useBulkDisbursementAction } from '../useBulkDisbursementAction'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ActionModal, Modal, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { MarkAsSentBulkProps } from './MarkAsSentBulk.types'

export function MarkAsSentBulk({
  isOpen,
  onClose,
  selectedDisbursementIds,
  refreshData,
  onSelectedDisbursementIdsChange,
  disbursementBatchId,
  approvedChequesCount
}: Readonly<MarkAsSentBulkProps>) {
  const { t } = useTranslation()
  const { disbursementsService } = useServices()
  const {
    summary: bulkSummary,
    isPending: isPendingBulk,
    execute: executeBulk,
    resetSummary: resetBulkSummary
  } = useBulkDisbursementAction(disbursementsService.bulkMarkDisbursementsAsSent, ids => ids)

  const [batchSummary, setBatchSummary] = useState<{ succeededCount: number; failedCount: number } | undefined>()

  const [{ isPending: isPendingBatch }, { mutateAsync: markApprovedChequeDisbursementsAsSent }] = useMutation(
    disbursementsService.markApprovedChequeDisbursementsAsSent
  )

  const isPending = isPendingBulk || isPendingBatch

  const disbursementCount = approvedChequesCount ?? selectedDisbursementIds.length

  const handleClose = () => {
    if (isPending) {
      return
    }

    refreshData?.()
    onSelectedDisbursementIdsChange(xor(selectedDisbursementIds, bulkSummary?.succeededDisbursementIds ?? []))
    resetBulkSummary()
    setBatchSummary(undefined)
    onClose()
  }

  const handleSubmit = async () => {
    if (disbursementBatchId) {
      setBatchSummary(undefined)
      await markApprovedChequeDisbursementsAsSent(disbursementBatchId)
      setBatchSummary({ succeededCount: disbursementCount, failedCount: 0 })

      return
    }

    if (selectedDisbursementIds.length === 0) {
      return
    }

    await executeBulk(selectedDisbursementIds)
  }

  const failedCount = bulkSummary?.failedDisbursementIds.length ?? batchSummary?.failedCount ?? 0
  const succeededCount = bulkSummary?.succeededDisbursementIds.length ?? batchSummary?.succeededCount ?? 0
  const hasSummary = !!(bulkSummary || batchSummary)

  const successModal = hasSummary && succeededCount > 0 && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.bulkDisbursementsMarkAsSent.result.success.title', { count: succeededCount })}
      description={
        succeededCount !== disbursementCount
          ? t('dialog.bulkDisbursementsMarkAsSent.result.success.description', {
              count: failedCount,
              allCount: disbursementCount
            })
          : undefined
      }
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="bulk-disbursements-mark-as-sent-success-modal"
      buttonTestId="bulk-disbursements-mark-as-sent-success-close-button"
    />
  )

  const errorModal = hasSummary && succeededCount === 0 && failedCount > 0 && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.bulkDisbursementsMarkAsSent.result.error.description', { count: failedCount })}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="bulk-disbursements-mark-as-sent-error-modal"
      buttonTestId="bulk-disbursements-mark-as-sent-error-close-button"
    />
  )

  const modalContent = successModal || errorModal || (
    <ActionModal
      title={t('dialog.bulkDisbursementsMarkAsSent.title', {
        count: disbursementCount
      })}
      primaryButtonText={t('button.confirm')}
      secondaryButtonText={t('button.cancel')}
      type="submit"
      loading={isPending}
      disabled={isPending || disbursementCount === 0}
      handleClose={handleClose}
      handleSubmit={handleSubmit}
      primaryBtnTestId="bulk-disbursements-mark-as-sent-submit-button"
      secondaryBtnTestId="bulk-disbursements-mark-as-sent-cancel-button"
    >
      <Text size="small">
        {t('dialog.bulkDisbursementsMarkAsSent.description', {
          count: disbursementCount
        })}
      </Text>
    </ActionModal>
  )

  return (
    <Modal open={isOpen} onClose={handleClose}>
      {modalContent}
    </Modal>
  )
}
